import React, { useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import axios from 'axios';
import './ExcelPaste.css'; // Importing CSS for styling
import { useDispatch, useSelector } from 'react-redux';
import { fetchClients } from '../store/clientsSlice';
import {
  buildSuggestedColumnMapping,
  DATA_TYPE_OPTIONS,
  findDuplicateDeliveryRows,
  getImportSourceLabel,
  getImportFieldDefinitions,
  getMissingRequiredFields,
  IMPORT_SOURCE_OPTIONS,
  normalizeTradeRows,
  transformTradeRows,
} from '../utils/tradeImport';
const apiUrl = process.env.REACT_APP_API_URL;

const extractRequestErrorMessage = (requestError) => {
  const responseData = requestError?.response?.data;
  if (typeof responseData === 'string' && responseData.trim() !== '') {
    return responseData;
  }
  if (Array.isArray(responseData) && responseData.length > 0) {
    return responseData.map((item) => item?.message || item?.code || 'Unknown error').join(', ');
  }
  return 'Error sending data to API.';
};

const ExcelPaste = () => {
  const dispatch = useDispatch();
  const { clientId } = useSelector((state) => state.app);
  const clientAccesses = useSelector((state) => state.user.profile?.clientAccesses || []);

  const [rawData, setRawData] = useState([]);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [dataType, setDataType] = useState('');
  const [importSource, setImportSource] = useState('angel');
  const [columnMapping, setColumnMapping] = useState({});

  useEffect(() => {
    dispatch(fetchClients());
  }, [dispatch]);

  const savedBrokerPreset = useMemo(
    () => clientAccesses.find((clientAccess) => clientAccess.clientId === clientId)?.brokerPreset || '',
    [clientAccesses, clientId],
  );

  const effectiveImportSource = savedBrokerPreset || importSource;

  const rawHeaders = useMemo(
    () => (rawData.length > 0
      ? Object.keys(rawData[0]).filter((header) => String(header ?? '').trim() !== '')
      : []),
    [rawData],
  );

  useEffect(() => {
    if (!dataType || rawHeaders.length === 0) {
      setColumnMapping({});
      return;
    }

    setColumnMapping(buildSuggestedColumnMapping(dataType, effectiveImportSource, rawHeaders));
  }, [dataType, effectiveImportSource, rawHeaders]);

  const handlePaste = (e) => {
    const pastedText = e.target.value;
    setText(pastedText);
    setSuccessMessage('');

    try {
      Papa.parse(pastedText, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setRawData(results.data);
          setError(''); // Clear any previous errors
        },
        error: () => {
          setError('Failed to parse data. Please check the format.');
          setRawData([]);
        }
      });
    } catch (parseError) {
      setError('An error occurred while parsing the data.');
      setRawData([]);
    }
  };

  const fieldDefinitions = useMemo(() => getImportFieldDefinitions(dataType), [dataType]);

  const data = useMemo(
    () => normalizeTradeRows(dataType, rawData, columnMapping),
    [columnMapping, dataType, rawData],
  );

  const transformedData = useMemo(() => transformTradeRows(dataType, data), [dataType, data]);

  const previewHeaders = useMemo(
    () => (data.length > 0
      ? Object.keys(data[0]).filter((key) => data.some((row) => row[key] !== null && row[key] !== ''))
      : []),
    [data],
  );

  const missingRequiredFields = useMemo(
    () => getMissingRequiredFields(dataType, columnMapping),
    [columnMapping, dataType],
  );

  const mappedFieldCount = useMemo(
    () => fieldDefinitions.filter((field) => columnMapping[field.key]).length,
    [columnMapping, fieldDefinitions],
  );

  const duplicateDeliveryRows = useMemo(
    () => (dataType === 'delivery' ? findDuplicateDeliveryRows(clientId, transformedData) : []),
    [clientId, dataType, transformedData],
  );

  const duplicateDeliveryMessage = useMemo(
    () => duplicateDeliveryRows.map((duplicate) => `${duplicate.label} rows ${duplicate.rowNumbers.join(', ')}`).join('; '),
    [duplicateDeliveryRows],
  );

  const handleColumnMappingChange = (fieldKey, selectedHeader) => {
    setColumnMapping((current) => ({
      ...current,
      [fieldKey]: selectedHeader,
    }));
    setError('');
    setSuccessMessage('');
  };

  const sendDataToApi = () => {
    if (!clientId || !dataType) {
      setError('Please select a client and data type.');
      return;
    }

    if (missingRequiredFields.length > 0) {
      setError(`Map the required fields before importing: ${missingRequiredFields.join(', ')}`);
      return;
    }

    if (transformedData.length === 0) {
      setError('No mapped rows are ready to import. Paste data and map at least one standard field.');
      return;
    }

    if (duplicateDeliveryRows.length > 0) {
      setError(`Excel contains duplicate delivery entries. Merge them before proceed: ${duplicateDeliveryMessage}`);
      return;
    }

    const payload = {
      clientId: clientId,
      tradeData: transformedData,
    };

    let apiEndpoint = '';
    switch (dataType) {
      case 'delivery':
        apiEndpoint = `${apiUrl}/api/trades/add/delivery`;
        break;
      case 'intraday':
        apiEndpoint = `${apiUrl}/api/trades/add/intraday`;
        break;
      case 'future':
        apiEndpoint = `${apiUrl}/api/trades/add/future`;
        break;
      case 'option':
        apiEndpoint = `${apiUrl}/api/trades/add/option`;
        break;
      case 'commodityoption':
        apiEndpoint = `${apiUrl}/api/trades/add/commodity/option`;
        break;
      case 'commodityfuture':
        apiEndpoint = `${apiUrl}/api/trades/add/commodity/future`;
        break;
      case 'capitalasset':
        apiEndpoint = `${apiUrl}/api/capital-assets/import`;
        break;
      default:
        setError('Invalid data type selected.');
        return;
    }

    if (dataType === 'capitalasset') {
      payload.transactionData = transformedData;
      delete payload.tradeData;
    }

    axios.post(apiEndpoint, payload)
      .then(response => {
        console.log('Data sent successfully', response.data);
        setError(''); // Clear any previous errors
        setWarningMessage('');
        if (dataType === 'delivery' && response.data && typeof response.data.insertedCount === 'number' && typeof response.data.updatedCount === 'number') {
          setSuccessMessage(`Delivery import completed: ${response.data.insertedCount} inserted, ${response.data.updatedCount} updated.`);
          if (response.data.warnings && response.data.warnings.length > 0) {
            setWarningMessage(response.data.warnings.join('\n'));
          }
          return;
        }
        setSuccessMessage(`Imported ${transformedData.length} row(s) using the ${getImportSourceLabel(effectiveImportSource)} mapping.`);
      })
      .catch((requestError) => {
        setError(extractRequestErrorMessage(requestError));
      });
  };

  return (
    <div className="excel-paste-container">
      <h2>Excel Data Import</h2>
      <p className="financial-page-subtitle">Paste any broker export, map it once, and send a canonical payload into the existing database models.</p>

      <div className="excel-paste-toolbar">
        <div className="form-group">
          <label htmlFor="dataTypeSelect">Select Data Type:</label>
          <select
            id="dataTypeSelect"
            value={dataType}
            onChange={(e) => {
              setDataType(e.target.value);
              setError('');
              setSuccessMessage('');
            }}
          >
            <option value="">--Select Data Type--</option>
            {DATA_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="importSourceSelect">Broker / Source Preset:</label>
          <select
            id="importSourceSelect"
            value={effectiveImportSource}
            disabled={Boolean(savedBrokerPreset)}
            onChange={(e) => {
              setImportSource(e.target.value);
              setError('');
              setSuccessMessage('');
            }}
          >
            {IMPORT_SOURCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {savedBrokerPreset ? (
            <div className="form-text">Saved from client profile: {getImportSourceLabel(savedBrokerPreset)}.</div>
          ) : clientId ? (
            <div className="form-text">No saved preset for this client yet. This selection applies to the current import only.</div>
          ) : (
            <div className="form-text">Select a client to reuse its saved broker preset automatically.</div>
          )}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="excelTextarea">Paste Excel Data:</label>
        <textarea
          id="excelTextarea"
          rows="10"
          cols="50"
          placeholder="Paste your Excel data here..."
          value={text}
          onChange={handlePaste}
        />
      </div>
      {successMessage && <p className="financial-message financial-message--success">{successMessage}</p>}
      {error && <p className="error financial-message financial-message--error">{error}</p>}
      {warningMessage && <pre className="financial-message financial-message--warning" style={{whiteSpace: 'pre-wrap'}}>{warningMessage}</pre>}

      {rawHeaders.length > 0 && dataType && (
        <div className="import-mapping-card">
          <div className="import-mapping-summary">
            <p><strong>Detected columns:</strong> {rawHeaders.join(', ')}</p>
            <p><strong>Mapped fields:</strong> {mappedFieldCount} of {fieldDefinitions.length}</p>
            <p>Unmapped columns are ignored. Required fields must be mapped before import.</p>
          </div>

          <div className="column-mapping-grid">
            {fieldDefinitions.map((field) => (
              <label key={field.key} className="column-mapping-field">
                <span>
                  {field.label}
                  {field.required && <strong className="column-mapping-required">Required</strong>}
                </span>
                <select
                  value={columnMapping[field.key] || ''}
                  onChange={(e) => handleColumnMappingChange(field.key, e.target.value)}
                >
                  <option value="">Ignore this field</option>
                  {rawHeaders.map((header) => (
                    <option key={header} value={header}>{header}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          {missingRequiredFields.length > 0 && (
            <p className="financial-message financial-message--warning">
              Required fields still missing: {missingRequiredFields.join(', ')}
            </p>
          )}

		  {duplicateDeliveryRows.length > 0 && (
			<p className="financial-message financial-message--warning">
			  Duplicate delivery rows detected: {duplicateDeliveryMessage}. Merge them before proceed.
			</p>
		  )}
        </div>
      )}

      {data.length > 0 && previewHeaders.length > 0 && (
        <div className="data-preview">
          <h3>Excel Data Preview</h3>
          <table className="data-table">
            <thead>
              <tr>
                {previewHeaders.map((key) => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index}>
                  {previewHeaders.map((header) => (
                    <td key={`${index}-${header}`}>{row[header]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <button
            className="submit-button"
            onClick={sendDataToApi}
            disabled={missingRequiredFields.length > 0 || transformedData.length === 0 || duplicateDeliveryRows.length > 0}
          >
            Send Data to API
          </button>
        </div>
      )}
    </div>
  );
};

export default ExcelPaste;
